/* sugar:assets/section-neighbors.js@2.0.0 */
/**
 * Section neighbours — what colour the section above or below a given one paints
 * against the edge they share.
 *
 * One copy serves every component that matches itself to its neighbours (the section
 * divider, the split background). Nothing here depends on a class name: callers hand in
 * a `.shopify-section` wrapper and get back the colour of the nearest painted neighbour.
 *
 *   window.SugarSectionNeighbors.above(wrap) -> { color, image } | null
 *   window.SugarSectionNeighbors.below(wrap) -> { color, image } | null
 *
 * `color` is always a solid `rgb(...)` — for a gradient neighbour it is the colour of
 * the stop that touches the shared edge, so a shape filled with it still meets the
 * neighbour seamlessly. `image` is set only when the neighbour's gradient runs across
 * the page (left to right): that one can be copied whole and still line up, because a
 * horizontal gradient looks the same at any height. Anything else leaves it null.
 */

(function () {
  'use strict';

  if (window.SugarSectionNeighbors) return;

  var MAX_HOPS = 20;

  function isOpaque(c) {
    return c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent';
  }

  /* Split a gradient's argument list on the commas that sit outside any parentheses. */
  function splitArgs(s) {
    var out = [], depth = 0, cur = '';
    for (var i = 0; i < s.length; i++) {
      var ch = s.charAt(i);
      if (ch === '(') depth++;
      else if (ch === ')') depth--;
      if (ch === ',' && depth === 0) {
        out.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    if (cur.trim()) out.push(cur.trim());
    return out;
  }

  function parseRgb(c) {
    var m = /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(c || '');
    if (!m) {
      m = /rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.%]+)\s*)?\)/.exec(c || '');
      if (!m) return null;
    }
    var a = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (/%$/.test(m[4] || '')) a = a / 100;
    return { r: +m[1], g: +m[2], b: +m[3], a: a };
  }

  /* Flatten a translucent stop onto the solid colour painted under the gradient. */
  function flatten(stop, under) {
    var s = parseRgb(stop);
    if (!s) return null;
    if (s.a >= 1) return 'rgb(' + Math.round(s.r) + ', ' + Math.round(s.g) + ', ' + Math.round(s.b) + ')';
    var u = isOpaque(under) ? parseRgb(under) : null;
    if (!u) return 'rgba(' + Math.round(s.r) + ', ' + Math.round(s.g) + ', ' + Math.round(s.b) + ', ' + s.a + ')';
    var mix = function (x, y) { return Math.round(x * s.a + y * (1 - s.a)); };
    return 'rgb(' + mix(s.r, u.r) + ', ' + mix(s.g, u.g) + ', ' + mix(s.b, u.b) + ')';
  }

  function sideToDeg(side) {
    var t = side.replace(/\s+/g, ' ').trim();
    if (t === 'top') return 0;
    if (t === 'right') return 90;
    if (t === 'bottom') return 180;
    if (t === 'left') return 270;
    // A corner keyword ("top right"): treat it as the diagonal it names.
    var v = /top/.test(t) ? -1 : /bottom/.test(t) ? 1 : 0;
    var h = /right/.test(t) ? 1 : /left/.test(t) ? -1 : 0;
    return (Math.atan2(h, -v) * 180 / Math.PI + 360) % 360;
  }

  function angleToDeg(v, unit) {
    var n = parseFloat(v);
    if (unit === 'turn') return n * 360;
    if (unit === 'rad') return n * 180 / Math.PI;
    if (unit === 'grad') return n * 0.9;
    return n;
  }

  /**
   * The colour a gradient shows along one edge of its box, and whether it can be copied
   * whole. `edge` is 'top' or 'bottom' — the side of the neighbour that touches us.
   */
  function gradientEdge(image, edge, under) {
    var m = /^(?:repeating-)?(linear|radial|conic)-gradient\((.*)\)$/.exec(image.trim());
    if (!m) return null;
    var kind = m[1];
    var parts = splitArgs(m[2]);
    var angle = 180;

    if (kind === 'linear') {
      var a = /^(-?[\d.]+)(deg|turn|rad|grad)$/.exec(parts[0] || '');
      var t = /^to\s+(.+)$/.exec(parts[0] || '');
      if (a) { angle = angleToDeg(a[1], a[2]); parts.shift(); }
      else if (t) { angle = sideToDeg(t[1]); parts.shift(); }
    } else if (parts.length && !/rgba?\(/.test(parts[0])) {
      parts.shift(); // shape, size, position or start angle
    }

    var stops = [];
    for (var i = 0; i < parts.length; i++) {
      var c = /rgba?\([^)]*\)/.exec(parts[i]);
      if (c) stops.push(c[0]);
    }
    if (!stops.length) return null;

    var first = stops[0], last = stops[stops.length - 1];

    if (kind === 'linear') {
      var rad = ((angle % 360) + 360) % 360 * Math.PI / 180;
      var down = -Math.cos(rad); // 180deg travels downward: +1
      if (Math.abs(down) < 0.05) {
        // Runs across the page: every row of pixels is the same, so copy it whole.
        return { color: flatten(first, under), image: image };
      }
      var atBottom = down > 0 ? last : first;
      return { color: flatten(edge === 'bottom' ? atBottom : (atBottom === last ? first : last), under), image: null };
    }

    // Radial and conic gradients reach their last stop at the edges of the box.
    return { color: flatten(last, under), image: null };
  }

  function readEl(el, edge) {
    var cs = getComputedStyle(el);
    var img = cs.backgroundImage;
    if (img && img !== 'none' && /gradient\(/.test(img)) {
      // The topmost layer is what shows. A photo on top hides any gradient under it.
      var top = splitArgs(img)[0];
      if (/gradient\(/.test(top)) {
        var g = gradientEdge(top, edge, cs.backgroundColor);
        if (g && g.color) return g;
      }
    }
    if (isOpaque(cs.backgroundColor)) return { color: cs.backgroundColor, image: null };
    var cv = cs.getPropertyValue('--color-background');
    if (cv && cv.trim()) return { color: 'rgb(' + cv.trim() + ')', image: null };
    return null;
  }

  /**
   * The painted colour of a section: its first real child's background if it has one
   * (sections usually paint the band on an inner wrapper), otherwise its own.
   */
  function getBg(w, edge) {
    if (!w) return null;

    // A section painted by a split background shows that layer's colour at its edge, not the
    // band colour under it: the first colour along the top, the second along the bottom.
    var split = w.querySelector('.sugar-split-background');
    if (split) {
      var own = split.style.getPropertyValue(edge === 'top' ? '--sb-a' : '--sb-b').trim();
      if (own && own !== 'initial') {
        if (/gradient\(/.test(own)) {
          var sg = gradientEdge(own, edge, null);
          if (sg && sg.color) return sg;
        } else {
          return { color: own, image: null };
        }
      }
    }

    var children = w.children;
    for (var i = 0; i < children.length; i++) {
      var el = children[i];
      var tag = el.tagName;
      if (tag === 'STYLE' || tag === 'LINK' || tag === 'SCRIPT' || tag === 'NOSCRIPT') continue;
      var r = readEl(el, edge);
      if (r) return r;
      break;
    }

    return readEl(w, edge);
  }

  /**
   * A sticky section that has not docked is floating over the page rather than sitting
   * in the flow, so its colour is not what the caller meets.
   */
  function isFloating(el) {
    if (typeof el.className !== 'string' || el.className.indexOf('sticky') === -1) return false;
    return !el.querySelector('[data-docked="true"]');
  }

  function findBg(wrap, prev) {
    var edge = prev ? 'bottom' : 'top';
    var s = prev ? wrap.previousElementSibling : wrap.nextElementSibling;
    for (var i = 0; i < MAX_HOPS && s; i++) {
      if (!isFloating(s)) {
        var bg = getBg(s, edge);
        if (bg) return bg;
      }
      s = prev ? s.previousElementSibling : s.nextElementSibling;
    }

    var p = wrap.parentElement;
    if (!p) return null;

    var ns = prev ? p.previousElementSibling : p.nextElementSibling;
    for (var j = 0; j < MAX_HOPS && ns; j++) {
      if (!isFloating(ns)) {
        var outerBg = getBg(ns, edge);
        if (outerBg) return outerBg;
      }
      ns = prev ? ns.previousElementSibling : ns.nextElementSibling;
    }
    return null;
  }

  window.SugarSectionNeighbors = {
    above: function (wrap) { return findBg(wrap, true); },
    below: function (wrap) { return findBg(wrap, false); },
    gradientEdge: gradientEdge
  };
})();
